import React, { useState } from 'react';
import { GripVertical, Copy, Trash2 } from 'lucide-react';
import { BLOCK_SCHEMAS } from './blocks';
import { PREVIEW_COMPONENTS } from './previews';
import type { Block, BlockType } from '@shared/types';

interface Props {
  blocks: Block[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onReorder: (fromIdx: number, toIdx: number) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onAddAtIndex: (type: BlockType, index: number) => void;
  previewMode: 'desktop' | 'mobile';
}

export default function PreviewPane({
  blocks, selectedId, onSelect, onReorder, onDelete, onDuplicate, onAddAtIndex, previewMode,
}: Props) {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const before = e.clientY < midY;
    setDropIndex(before ? idx : idx + 1);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    let payload: { kind?: string; blockType?: BlockType; blockId?: string } | null = null;
    try {
      payload = JSON.parse(e.dataTransfer.getData('text/plain') || '{}');
    } catch {}
    if (payload?.kind === 'new' && payload.blockType && dropIndex !== null) {
      onAddAtIndex(payload.blockType, dropIndex);
    } else if (payload?.kind === 'reorder' && payload.blockId && dropIndex !== null) {
      const fromIdx = blocks.findIndex((b) => b.id === payload!.blockId);
      if (fromIdx !== -1) {
        const adjustedDrop = dropIndex > fromIdx ? dropIndex - 1 : dropIndex;
        if (adjustedDrop !== fromIdx) onReorder(fromIdx, adjustedDrop);
      }
    }
    setDraggedId(null);
    setDropIndex(null);
  };

  const containerWidth = previewMode === 'mobile' ? 380 : 600;

  return (
    <div className="flex-1 overflow-y-auto bg-slate-100" onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}>
      <div className="py-8 px-4" style={{ background: '#EFF5FA', minHeight: '100%' }}>
        <div className="mx-auto" style={{ maxWidth: containerWidth, transition: 'max-width 0.2s ease' }}>
          {blocks.length === 0 && (
            <div className="text-center py-20 text-slate-400 text-sm">
              <div className="mb-2 opacity-60">No blocks yet</div>
              <div>Drag a block from the left to start building</div>
            </div>
          )}
          {blocks.map((block, idx) => {
            const Component = PREVIEW_COMPONENTS[block.type];
            const isSelected = selectedId === block.id;
            const showDropAbove = dropIndex === idx;
            return (
              <React.Fragment key={block.id}>
                {showDropAbove && <div className="h-1 bg-blue-500 rounded-full mb-2 -mt-1 shadow-md shadow-blue-200" />}
                <div
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', JSON.stringify({ kind: 'reorder', blockId: block.id }));
                    setDraggedId(block.id);
                  }}
                  onDragEnd={() => { setDraggedId(null); setDropIndex(null); }}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onClick={() => onSelect(block.id)}
                  className={`relative group cursor-pointer transition-all ${draggedId === block.id ? 'opacity-30' : ''}`}
                  style={{
                    outline: isSelected ? '2px solid #153EA7' : '2px solid transparent',
                    outlineOffset: 2,
                    borderRadius: 10,
                    marginBottom: 0,
                  }}
                >
                  <div className={`absolute -top-3 left-2 z-10 flex items-center gap-0.5 bg-white border border-slate-200 rounded-md shadow-sm px-1 py-0.5 ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 px-1.5 cursor-grab active:cursor-grabbing flex items-center gap-1">
                      <GripVertical size={10} />
                      {BLOCK_SCHEMAS[block.type]?.label}
                    </span>
                    <button onClick={(e) => { e.stopPropagation(); onDuplicate(block.id); }} className="p-1 hover:bg-slate-100 rounded text-slate-500" title="Duplicate">
                      <Copy size={11} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(block.id); }} className="p-1 hover:bg-red-50 rounded text-slate-500 hover:text-red-600" title="Delete">
                      <Trash2 size={11} />
                    </button>
                  </div>
                  <Component data={block.data} />
                </div>
              </React.Fragment>
            );
          })}
          {dropIndex === blocks.length && <div className="h-1 bg-blue-500 rounded-full mt-2 shadow-md shadow-blue-200" />}
          <div onDragOver={(e) => { e.preventDefault(); setDropIndex(blocks.length); }} className="h-12" />
        </div>
      </div>
    </div>
  );
}
